import React, { useState } from 'react';
import {
  Box,
  Card,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  Button,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useAuthContext } from 'src/auth/hooks';
import PermissionBasedGuard from 'src/auth/guard/permission-based-guard';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs/custom-breadcrumbs';
import Iconify from 'src/components/iconify';
import { useBoolean } from 'src/hooks/use-boolean';
import { joinErrors } from 'src/api/client';
import { generateDateOptions } from './selected-food-table';

// ----------------------------------------------------------------------

// Box sticker dimensions
const boxStickers = [
  { height: 297, width: 210, title: 'A4', key: 'a4', color: 'rgba(0, 167, 111, 0.08)' },
  { height: 175, width: 100, title: 'H175 × W100', key: '100x175', color: 'rgba(0, 167, 111, 0.08)' },
  { height: 150, width: 100, title: 'H150 × W100', key: '100x150', color: 'rgba(0, 167, 111, 0.08)' },
  { height: 100, width: 100, title: 'H100 × W100', key: '100x100', color: 'rgba(0, 167, 111, 0.08)' },
  { height: 75, width: 100, title: 'H75 × W100', key: '100x75', color: 'rgba(0, 167, 111, 0.08)' },
];

// Food sticker dimensions
const foodStickers = [
  { height: 100, width: 100, title: 'H100 × W100', key: '100x100', color: 'rgba(0, 167, 111, 0.08)' },
  { height: 50, width: 75, title: 'H50 × W75', key: '75x50', color: 'rgba(0, 167, 111, 0.08)' },
  { height: 50, width: 50, title: 'H50 × W50', key: '50x50', color: 'rgba(0, 167, 111, 0.08)' },
  { height: 20, width: 40, title: 'H20 × W40', key: '40x20', color: 'rgba(0, 167, 111, 0.08)' },
];

export default function SelectedFoodStickersPage({ restaurantId }: { restaurantId?: string }) {
  const { user } = useAuthContext();
  const { enqueueSnackbar } = useSnackbar();

  // Generate date options
  const dateOptions = React.useMemo(() => generateDateOptions(), []);

  // Loading state for download operations
  const isLoading = useBoolean(false);

  // Selected sticker values for tracking current operation
  const [selectedValue, setSelectedValue] = useState<{
    type: 'box' | 'food';
    key: string;
    width: number;
    height: number;
  } | null>(null);

  // Date and content deposition selection
  // Default to the last date in the list (latest available date)
  const [selectedDate, setSelectedDate] = useState<string>(
    dateOptions.length > 0 ? dateOptions[dateOptions.length - 1].value : ''
  );

  // Custom dimensions
  const [customBoxWidth, setCustomBoxWidth] = useState<number>(100);
  const [customBoxHeight, setCustomBoxHeight] = useState<number>(150);
  const [customFoodWidth, setCustomFoodWidth] = useState<number>(50);
  const [customFoodHeight, setCustomFoodHeight] = useState<number>(50);

  // Handle date change
  const handleDateChange = (event: SelectChangeEvent<string>) => {
    setSelectedDate(event.target.value);
  };

  // Generic function to handle sticker downloads
  const handleDownloadStickers = async ({
    type,
    key,
    width,
    height,
    minWidth,
    minHeight,
    validationMessage
  }: {
    type: 'box' | 'food',
    key: string,
    width: number,
    height: number,
    minWidth: number,
    minHeight: number,
    validationMessage?: string
  }) => {
    // Validate date
    if (!selectedDate) {
      enqueueSnackbar('Please select a date first', { variant: 'warning' });
      return;
    }

    // Validate dimensions
    if (width < minWidth || height < minHeight) {
      enqueueSnackbar(
        validationMessage ||
        `Dimensions: Width must be at least ${minWidth}mm and height at least ${minHeight}mm`,
        { variant: 'error' }
      );
      return;
    }

    if (width > 300 || height > 300) {
      enqueueSnackbar('Dimensions should not exceed 300mm', { variant: 'error' });
      return;
    }

    try {
      isLoading.onTrue();
      setSelectedValue({ type, key, width, height });

      // Determine endpoint based on sticker type
      const endpoint = type === 'box' ? '/partners/stickers/customers/' : '/partners/stickers/foods/';

      // Use direct fetch to avoid any processing of the PDF blob
      const baseUrl = import.meta.env.VITE_HOST_API;
      const token = localStorage.getItem('accessToken');
      
      const url = new URL(`${baseUrl}${endpoint}`);
      url.searchParams.append('date', selectedDate);
      url.searchParams.append('width', width.toString());
      url.searchParams.append('height', height.toString());
      url.searchParams.append('content_deposition', 'attachment');

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Get the raw blob data
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', `${type}-stickers-${selectedDate}-${width}x${height}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

    } catch (error) {
      console.error(`Error downloading ${type} stickers:`, error);
      enqueueSnackbar(`Error downloading ${type} stickers: ${joinErrors(error)}`, { variant: 'error' });
    } finally {
      isLoading.onFalse();
      setSelectedValue(null);
    }
  };

  // Handler for preset box stickers
  const handleDownloadBoxStickers = (sticker: typeof boxStickers[0]) => {
    handleDownloadStickers({
      type: 'box',
      key: sticker.key,
      width: sticker.width,
      height: sticker.height,
      minWidth: 100,
      minHeight: 75
    });
  };

  // Handler for preset food stickers
  const handleDownloadFoodStickers = (sticker: typeof foodStickers[0]) => {
    handleDownloadStickers({
      type: 'food',
      key: sticker.key,
      width: sticker.width,
      height: sticker.height,
      minWidth: 40,
      minHeight: 20
    });
  };

  // Handler for custom box stickers
  const handleDownloadCustomBoxStickers = () => {
    handleDownloadStickers({
      type: 'box',
      key: 'custom',
      width: customBoxWidth,
      height: customBoxHeight,
      minWidth: 100,
      minHeight: 75,
      validationMessage: 'Box sticker dimensions: Width must be at least 100 mm and height at least 75 mm'
    });
  };

  // Handler for custom food stickers
  const handleDownloadCustomFoodStickers = () => {
    handleDownloadStickers({
      type: 'food',
      key: 'custom',
      width: customFoodWidth,
      height: customFoodHeight,
      minWidth: 40,
      minHeight: 20,
      validationMessage: 'Food sticker dimensions: Width must be at least 40 mm and height at least 20 mm'
    });
  };

  return (
    <PermissionBasedGuard hasContent permissions={['partners:read:selectedfood']}>
      <Container maxWidth={false}>
      <CustomBreadcrumbs
          heading="Food Stickers"
          sx={{ mb: 2 }}
          links={[{}]}
        />

        {/* Date Selection and Content Deposition */}
        <Card sx={{ mb: 5, p: 3 }}>
          <Typography variant="h6" sx={{ mb: 3 }}>
            Select Date
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={12}>
              <FormControl fullWidth required>
                <InputLabel id="date-select-label">Date</InputLabel>
                <Select
                  labelId="date-select-label"
                  value={selectedDate}
                  onChange={handleDateChange}
                  label="Date *"
                  MenuProps={{
                    PaperProps: {
                      sx: { maxHeight: 400 },
                    },
                  }}
                >
                  {dateOptions.map((option) => (
                    <MenuItem
                      key={option.value}
                      value={option.value}
                      sx={option.isToday ? {
                        fontWeight: 'bold',
                        color: 'primary.main',
                        backgroundColor: 'rgba(0, 171, 85, 0.08)',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 171, 85, 0.15)',
                        }
                      } : {}}
                    >
                      {option.isToday ? (
                        <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                          <Iconify icon="mdi:calendar-today" sx={{ mr: 1, color: 'primary.main' }} />
                          {option.label}
                        </Box>
                      ) : (
                        option.label
                      )}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {!selectedDate && (
            <Typography color="error" sx={{ mt: 2 }}>
              Please select a date to download stickers
            </Typography>
          )}
        </Card>

        {/* Box Stickers */}
        <Card sx={{ mb: 5, p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Box Stickers
          </Typography>
          <Typography variant="subtitle2" sx={{ mb: 3, color: 'text.secondary' }}>
            Click on a dimension to download box stickers in PDF format
          </Typography>

          <Grid container spacing={3}>
            {boxStickers.map((sticker) => (
              <Grid item key={sticker.key}>
                <Card
                  onClick={() => handleDownloadBoxStickers(sticker)}
                  sx={{
                    width: `${sticker.width * 0.8}px`,
                    height: `${sticker.height * 0.8}px`,
                    minWidth: '160px',
                    minHeight: '120px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: sticker.color,
                    cursor: selectedDate ? 'pointer' : 'not-allowed',
                    opacity: selectedDate ? 1 : 0.6,
                    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                    '&:hover': {
                      boxShadow: selectedDate ? '0px 8px 16px rgba(0, 0, 0, 0.2)' : 'none',
                      backgroundColor: selectedDate ? 'rgba(0, 167, 111, 0.12)' : sticker.color,
                    },
                  }}
                >
                  {isLoading.value &&
                   selectedValue?.type === 'box' &&
                   selectedValue?.key === sticker.key ? (
                    <CircularProgress thickness={1} variant="indeterminate" size={24} />
                  ) : (
                    <Typography fontWeight={800} fontSize="18px" color="#00A76F">
                      {sticker.title}
                    </Typography>
                  )}
                </Card>
              </Grid>
            ))}

            {/* Custom Box Dimensions Card */}
            <Grid item>
              <Card
                sx={{
                  width: '280px',
                  minHeight: '200px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  padding: 3,
                  backgroundColor: 'rgba(0, 167, 111, 0.04)',
                  cursor: selectedDate ? 'default' : 'not-allowed',
                  opacity: selectedDate ? 1 : 0.6,
                  boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                }}
              >
                <Box>
                  <Typography fontWeight={800} fontSize="18px" color="#00A76F" gutterBottom>
                    Custom Dimensions
                  </Typography>

                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Height (mm)"
                        value={customBoxHeight}
                        onChange={(e) => setCustomBoxHeight(Number(e.target.value) || 0)}
                        inputProps={{ min: 75, max: 300 }}
                        size="small"
                        disabled={!selectedDate || isLoading.value}
                        helperText="Min: 75mm"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Width (mm)"
                        value={customBoxWidth}
                        onChange={(e) => setCustomBoxWidth(Number(e.target.value) || 0)}
                        inputProps={{ min: 100, max: 300 }}
                        size="small"
                        disabled={!selectedDate || isLoading.value}
                        helperText="Min: 100mm"
                      />
                    </Grid>
                  </Grid>
                </Box>

                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleDownloadCustomBoxStickers}
                  disabled={!selectedDate || isLoading.value}
                  sx={{ mt: 2 }}
                  startIcon={
                    isLoading.value && selectedValue?.type === 'box' && selectedValue?.key === 'custom' ? (
                      <CircularProgress color="inherit" size={16} thickness={2} />
                    ) : (
                      <Iconify icon="eva:download-fill" />
                    )
                  }
                >
                  Download
                </Button>
              </Card>
            </Grid>
          </Grid>
        </Card>

        {/* Food Stickers */}
        <Card sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Food Stickers
          </Typography>
          <Typography variant="subtitle2" sx={{ mb: 3, color: 'text.secondary' }}>
            Click on a dimension to download food stickers in PDF format
          </Typography>

          <Grid container spacing={3}>
            {foodStickers.map((sticker) => (
              <Grid item key={sticker.key}>
                <Card
                  onClick={() => handleDownloadFoodStickers(sticker)}
                  sx={{
                    width: Math.max(sticker.width * 3, 160),
                    height: Math.max(sticker.height * 3, 120),
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: sticker.color,
                    cursor: selectedDate ? 'pointer' : 'not-allowed',
                    opacity: selectedDate ? 1 : 0.6,
                    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                    '&:hover': {
                      boxShadow: selectedDate ? '0px 8px 16px rgba(0, 0, 0, 0.2)' : 'none',
                      backgroundColor: selectedDate ? 'rgba(0, 167, 111, 0.12)' : sticker.color,
                    },
                  }}
                >
                  {isLoading.value &&
                   selectedValue?.type === 'food' &&
                   selectedValue?.key === sticker.key ? (
                    <CircularProgress thickness={1} variant="indeterminate" size={24} />
                  ) : (
                    <Typography fontWeight={800} fontSize="18px" color="#00A76F">
                      {sticker.title}
                    </Typography>
                  )}
                </Card>
              </Grid>
            ))}

            {/* Custom Food Dimensions Card */}
            <Grid item>
              <Card
                sx={{
                  width: '280px',
                  minHeight: '200px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  padding: 3,
                  backgroundColor: 'rgba(0, 167, 111, 0.04)',
                  cursor: selectedDate ? 'default' : 'not-allowed',
                  opacity: selectedDate ? 1 : 0.6,
                  boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                }}
              >
                <Box>
                  <Typography fontWeight={800} fontSize="18px" color="#00A76F" gutterBottom>
                    Custom Dimensions
                  </Typography>

                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Height (mm)"
                        value={customFoodHeight}
                        onChange={(e) => setCustomFoodHeight(Number(e.target.value) || 0)}
                        inputProps={{ min: 20, max: 300 }}
                        size="small"
                        disabled={!selectedDate || isLoading.value}
                        helperText="Min: 20mm"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Width (mm)"
                        value={customFoodWidth}
                        onChange={(e) => setCustomFoodWidth(Number(e.target.value) || 0)}
                        inputProps={{ min: 40, max: 300 }}
                        size="small"
                        disabled={!selectedDate || isLoading.value}
                        helperText="Min: 40mm"
                      />
                    </Grid>
                  </Grid>
                </Box>

                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleDownloadCustomFoodStickers}
                  disabled={!selectedDate || isLoading.value}
                  sx={{ mt: 2 }}
                  startIcon={
                    isLoading.value && selectedValue?.type === 'food' && selectedValue?.key === 'custom' ? (
                      <CircularProgress color="inherit" size={16} thickness={2} />
                    ) : (
                      <Iconify icon="eva:download-fill" />
                    )
                  }
                >
                  Download
                </Button>
              </Card>
            </Grid>
          </Grid>
        </Card>
      </Container>
    </PermissionBasedGuard>
  );
}
